/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Spin } from 'antd'

import colors from '@sportninja/common/constants/appColors'
import {
  gameTimeZone,
  groupGamesByDay,
} from '@sportninja/common/features/game/gameListView/gameDayBucket'
import type {
  GameLike,
  VenueRef,
} from '@sportninja/common/features/game/gameListView/types'
import { spansMultipleZones } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'
import { bucketEventsByDay } from '@sportninja/common/features/event/calendarItems'
import type { SnEvent } from '@sportninja/common/features/event/types'

import { font, weightRegular, weightSemiBold } from 'src/components/css'
import type { Game } from '../game-list-row/types'
import CalendarGameCard from './CalendarGameCard'
import CalendarEventCard from './CalendarEventCard'
import { formatCivilDay, todayCivilDate } from './buildCalendarDays'
import { calendarLabel } from './calendarI18n'
import { useAnchorDate, useRangeChangeNotifier } from './useAnchorDate'

const UNASSIGNED = '__unassigned__'

export interface DayTimeGridProps {
  items: Game[]
  /**
   * Rendered in a band of their own above the surface columns — see the band
   * itself for why they cannot join a column.
   */
  events?: SnEvent[]
  isSoccer: boolean
  isHockey: boolean
  loading: boolean
  /** Union of every venue this view knows about — see `useVenueOptions`. */
  venuesById: Map<string, VenueRef>
  anchorDate?: string | null
  onAnchorDateChange?: (date: string) => void
  onDayChange?: (startDate: string, endDate: string) => void
}

interface Column {
  key: string
  venueLabel: string
  facilityLabel: string
  games: Game[]
}

/**
 * Groups a day's games into one column per surface.
 *
 * The surface identity comes from the game itself first and the venue map
 * second. A partial `/venues/active` response is common — it only lists venues
 * already carrying games, with facilities filtered to the ones already used —
 * and looking the facility up ONLY in that response would file a game whose row
 * plainly names its rink under "Unassigned".
 *
 * Every game lands in exactly one column. There is no filter step that can drop
 * one, which the scheduler wizard's DayGrid does when a facility fails to match.
 */
export const buildDayColumns = (
  games: Game[],
  venuesById: Map<string, VenueRef>,
  unassignedLabel: string,
  unknownVenueLabel: string
): Column[] => {
  const columns = new Map<string, Column>()

  for (const game of games) {
    const asGame = game as unknown as GameLike
    const venueId = asGame.venue?.id ?? ''
    const facilityId = asGame.facility?.id ?? UNASSIGNED
    const key = `${venueId}::${facilityId}`

    let column = columns.get(key)
    if (column === undefined) {
      const knownVenue = venueId ? venuesById.get(venueId) : undefined
      const venueLabel =
        asGame.venue?.name_full ??
        asGame.venue?.name ??
        knownVenue?.name_full ??
        knownVenue?.name ??
        unknownVenueLabel

      //  The game's own facility name wins; the venue map is the fallback for a
      //  row that carries only an id.
      const facilityLabel =
        facilityId === UNASSIGNED
          ? unassignedLabel
          : asGame.facility?.name ??
            (knownVenue?.facilities ?? []).find(
              (facility) => facility.id === facilityId
            )?.name ??
            unassignedLabel

      column = { key, venueLabel, facilityLabel, games: [] }
      columns.set(key, column)
    }
    column.games.push(game)
  }

  return Array.from(columns.values()).sort((a, b) =>
    `${a.venueLabel} ${a.facilityLabel}`.localeCompare(
      `${b.venueLabel} ${b.facilityLabel}`
    )
  )
}

const DayTimeGrid = ({
  items,
  events,
  isSoccer,
  isHockey,
  loading,
  venuesById,
  anchorDate,
  onAnchorDateChange,
  onDayChange,
}: DayTimeGridProps) => {
  'use memo'

  const { anchor, setValue, value } = useAnchorDate(
    anchorDate,
    onAnchorDateChange
  )

  useRangeChangeNotifier(
    anchor.toFormat('yyyy-MM-dd'),
    anchor.plus({ days: 1 }).toFormat('yyyy-MM-dd'),
    onDayChange
  )

  const goToDay = (offset: number) =>
    setValue(anchor.plus({ days: offset }).toFormat('yyyy-MM-dd'))

  const bucket = groupGamesByDay(items as unknown as GameLike[]).find(
    (candidate) => candidate.dayKey === value
  )
  const timed = (bucket?.games ?? []) as unknown as Game[]
  const tbd = (bucket?.tbdGames ?? []) as unknown as Game[]
  const dayEvents = bucketEventsByDay(events).get(value) ?? []

  const columns = buildDayColumns(
    timed,
    venuesById,
    calendarLabel('calendarUnassigned'),
    calendarLabel('calendarUnknownVenue')
  )

  const zones = [...timed, ...tbd].map((game) =>
    gameTimeZone(game as unknown as GameLike)
  )
  const mixedZones = spansMultipleZones(zones)

  return (
    <div css={containerStyles} data-calendar-grid data-testid='day-time-grid'>
      <div css={navStyles}>
        <button
          type='button'
          css={navBtnStyles}
          onClick={() => goToDay(-1)}
          aria-label={calendarLabel('calendarPrevDay')}
        >
          &#8249;
        </button>
        <span css={titleStyles}>
          {formatCivilDay(value, 'cccc, LLLL d, yyyy')}
        </span>
        <button
          type='button'
          css={navBtnStyles}
          onClick={() => goToDay(1)}
          aria-label={calendarLabel('calendarNextDay')}
        >
          &#8250;
        </button>
        <button
          type='button'
          css={todayBtnStyles}
          onClick={() => setValue(todayCivilDate())}
          aria-label={calendarLabel('calendarGoToToday')}
        >
          {calendarLabel('calendarToday')}
        </button>
      </div>

      {mixedZones && (
        <p css={zoneNoticeStyles}>{calendarLabel('calendarMixedZones')}</p>
      )}

      {loading ? (
        <div css={loadingStyles}>
          <Spin size='small' />
        </div>
      ) : (
        <>
          {/*
            TBD games get their own band ABOVE the surfaces. They have no time
            and so no place in a time-ordered column — dropping them in at the
            sentinel hour would show a phantom 11:58pm game.
          */}
          {tbd.length > 0 && (
            <section css={tbdBandStyles} data-testid='day-tbd-band'>
              <h4 css={tbdTitleStyles}>
                {calendarLabel('calendarTbdBand', { count: tbd.length })}
              </h4>
              <div css={tbdGamesStyles}>
                {tbd.map((game) => (
                  <CalendarGameCard
                    key={game.id}
                    game={game}
                    isSoccer={isSoccer}
                    isHockey={isHockey}
                  />
                ))}
              </div>
            </section>
          )}

          {/*
            Events get a band of their own, for the same reason TBD games do:
            the columns below are one-per-SURFACE, and an event carries no
            facility to file it under. Forcing them into an "Unassigned" column
            would put them beside games that genuinely have no surface set,
            conflating "no rink chosen yet" with "not a rink booking at all".
          */}
          {dayEvents.length > 0 && (
            <section data-testid='day-events-band'>
              {/*
                Built from the SURFACE COLUMN's own parts rather than the TBD
                band's: a centred header block over a stacked body. The band
                laid its cards out as a wrapping row of fixed-width chips,
                which read as a different kind of object from the games
                directly beneath it. Events are the same kind of thing as a
                game — one per row, full width — so they are shaped like one.
              */}
              {/*
                The surface header below stacks a 14px venue name over a 13px
                facility name; this section has only the one label.

                To match its height WITHOUT computing a pixel total (no global
                line-height is set, so the labels use the browser's
                font-dependent `normal` and any number here would be a guess),
                it reproduces that second line box — but splits it into a blank
                half ABOVE the label and a blank half BELOW. Same total height
                as the neighbouring header, and the visible label ends up
                centred rather than pushed to the top, which is what a single
                trailing blank line did.
              */}
              <div css={columnHeaderStyles}>
                <div css={halfLineSpacerStyles} aria-hidden='true'>
                  {'\u00A0'}
                </div>
                <div css={venueLabelStyles}>
                  {calendarLabel('calendarEventsBand', {
                    count: dayEvents.length,
                  })}
                </div>
                <div css={halfLineSpacerStyles} aria-hidden='true'>
                  {'\u00A0'}
                </div>
              </div>
              <div css={eventsBodyStyles}>
                {dayEvents.map((event) => (
                  <CalendarEventCard key={event.id} event={event} />
                ))}
              </div>
            </section>
          )}

          {columns.length === 0 && dayEvents.length === 0 ? (
            <div css={emptyStyles}>{calendarLabel('calendarNoGames')}</div>
          ) : columns.length === 0 ? null : (
            <div css={columnsStyles}>
              {columns.map((column) => (
                <div key={column.key} css={columnStyles}>
                  <div css={columnHeaderStyles}>
                    <div css={venueLabelStyles}>{column.venueLabel}</div>
                    <div css={facilityLabelStyles}>{column.facilityLabel}</div>
                  </div>
                  <div css={columnBodyStyles}>
                    {column.games.map((game) => (
                      <CalendarGameCard
                        key={game.id}
                        game={game}
                        isSoccer={isSoccer}
                        isHockey={isHockey}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  background: ${colors.SECONDARY_500};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const navStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 14px 16px;
  background: ${colors.SECONDARY_300};
`

const navBtnStyles = css`
  ${font.body}
  background: transparent;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 6px;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
  padding: 4px 10px;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${colors.SECONDARY_200};
    color: ${colors.NEUTRAL_0};
  }
  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 1px;
  }
`

const todayBtnStyles = css`
  ${navBtnStyles};
  font-size: 13px;
  padding: 4px 12px;
  margin-left: 8px;
`

//  Body font — see the note on `monthTitleStyles` in CalendarGrid.
//
//  Wider min-width than the month/week titles: this one renders the longest
//  string of the three ("Wednesday, September 30, 2026"), and the body face is
//  wider than the condensed title face it replaced. Too narrow and the label
//  outgrows its box, pushing the ‹ › arrows around as the day changes.
const titleStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 18px;
  color: ${colors.NEUTRAL_0};
  min-width: 310px;
  text-align: center;
`

const zoneNoticeStyles = css`
  ${font.body}
  ${weightRegular}
  margin: 0;
  padding: 8px 16px;
  font-size: 14px;
  color: ${colors.NEUTRAL_50};
  background: ${colors.SECONDARY_400};
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const tbdBandStyles = css`
  padding: 10px 16px;
  border-bottom: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_400};
`

const tbdTitleStyles = css`
  ${font.body}
  ${weightSemiBold}
  margin: 0 0 8px;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.NEUTRAL_50};
`

const tbdGamesStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  > * {
    flex: 0 1 260px;
  }
`

const columnsStyles = css`
  display: flex;
  align-items: stretch;
  overflow-x: auto;
`

const columnStyles = css`
  flex: 1 0 220px;
  min-width: 220px;
  border-right: 1px solid ${colors.SECONDARY_200};

  &:last-of-type {
    border-right: none;
  }
`

const columnHeaderStyles = css`
  padding: 10px 8px;
  background: ${colors.SECONDARY_400};
  border-bottom: 1px solid ${colors.SECONDARY_200};
  text-align: center;
`

const venueLabelStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 14px;
  color: ${colors.NEUTRAL_0};
`

const facilityLabelStyles = css`
  ${font.body}
  ${weightRegular}
  font-size: 13px;
  color: ${colors.NEUTRAL_70};
`

/*
 * Half of the facility label's line box, used twice — once above the events
 * header's label and once below it.
 *
 * Two halves rather than one full trailing line: together they add exactly the
 * height the neighbouring surface header gets from its second label, so the two
 * headers stay level, while splitting them leaves the visible label centred
 * instead of sitting against the top edge.
 *
 * Derived from `facilityLabelStyles`' own font size rather than a px constant,
 * so it tracks that label if it is ever restyled.
 */
const halfLineSpacerStyles = css`
  ${font.body}
  ${weightRegular}
  font-size: 13px;
  line-height: 0.6;
`

const columnBodyStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  min-height: 320px;
`

/*
 * `columnBodyStyles` without the `min-height`: that number reserves a full
 * day's height for a surface column so the columns stay level with each other.
 * The events section spans the whole width and has no sibling to line up with,
 * so inheriting it would leave a large empty gap under one or two events.
 *
 * Background is left to the container, matching the game columns — the band
 * treatment it had before (a filled `SECONDARY_400` panel) is what made this
 * section read as a separate widget rather than as more of the same list.
 */
const eventsBodyStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const emptyStyles = css`
  ${font.body}
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  font-size: 14px;
  color: ${colors.NEUTRAL_50};
`

const loadingStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
`

export default DayTimeGrid
